import {Button, Card, Col, Modal, Row} from 'react-bootstrap'
import {Fragment, useEffect, useMemo, useRef, useState} from 'react'
import {useLazyQuery} from '@apollo/client'
import {toast} from 'react-toastify'
import {copyToClipboard} from '../../../../../utils/clipboardUtils'
import {GET_ONE_POLICY_API} from '../../../gql/queries/policiesApiQueries'
import {GET_MARKET_PLACE_2} from '../../../gql/queries/leadsQuery'
import {IResOnePolicyApi, NodePolicy} from '../../../types/policiesApi'
import {IResTest} from '../../../types/leads'
import {Plan} from '../../oneLead/oneleadTypes'
import {parseId} from '../../../helpers'
import {getMetalLevelValue} from '../../../helpers/consts'
import {
  buildMarketplaceLookupVariables,
  buildPolicyCardData,
  canLookupMarketplaceCardData,
  parseMarketplacePlanResponse,
} from './idCardData'
import {openPrintableMemberIdCard} from './idCardPrint'

interface ModalMemberIdCardProps {
  isOpen: boolean
  onClose: () => void
  id: string
}

const hasDisplayValue = (value?: string | null) => {
  const text = String(value || '').trim()
  return text.length > 0 && text.toUpperCase() !== 'N/A' && text !== 'N/A / N/A'
}

export const ModalMemberIdCard: React.FC<ModalMemberIdCardProps> = ({isOpen, onClose, id}) => {
  const [info, setInfo] = useState<NodePolicy | null>(null)
  const [marketPlan, setMarketPlan] = useState<Plan | null>(null)
  const [marketLookupError, setMarketLookupError] = useState<string | null>(null)
  const lookupKeyRef = useRef<string>('')

  const [gqlGetPolicy, {data: dataPolicy, loading: loadingPolicy, error: errorPolicy}] =
    useLazyQuery<IResOnePolicyApi>(GET_ONE_POLICY_API, {fetchPolicy: 'no-cache'})
  const [gqlGetMarketplace, {data: dataMarketplace, loading: loadingMarketplace}] =
    useLazyQuery<IResTest>(GET_MARKET_PLACE_2, {fetchPolicy: 'no-cache'})

  useEffect(() => {
    if (!isOpen || !id) return
    gqlGetPolicy({variables: {id: parseId(id)}})
  }, [gqlGetPolicy, id, isOpen])

  useEffect(() => {
    if (!dataPolicy?.policiesById) return
    setInfo(dataPolicy.policiesById)
    setMarketPlan(null)
    setMarketLookupError(null)
    lookupKeyRef.current = ''
  }, [dataPolicy])

  useEffect(() => {
    if (!dataMarketplace?.marketplace2) return
    const {plan, errorMessage} = parseMarketplacePlanResponse(dataMarketplace.marketplace2, {
      planName: info?.planName,
      issuerName: info?.carrier?.name,
      metalLevel: info ? getMetalLevelValue(String(info.metalLevel || '')) : '',
    })
    setMarketPlan(plan)
    setMarketLookupError(errorMessage)
  }, [dataMarketplace, info])

  useEffect(() => {
    if (!info) return
    const variables = buildMarketplaceLookupVariables(info)
    if (!variables) return

    const lookupKey = JSON.stringify({
      policyId: info.id,
      cmsPlanId: info.CMSPlanId,
      planName: info.planName,
      carrier: info.carrier?.name,
      metalLevel: info.metalLevel,
      state: info.stateEU?.code,
      effectiveDate: info.effectiveDate,
      membersCount: info.memberPolicySet?.edges?.length || 0,
    })
    if (lookupKeyRef.current === lookupKey) return

    lookupKeyRef.current = lookupKey
    setMarketLookupError(null)
    gqlGetMarketplace({variables})
  }, [gqlGetMarketplace, info])

  const cardData = useMemo(() => {
    if (!info) return null
    return buildPolicyCardData(info, marketPlan)
  }, [info, marketPlan])

  const frontRows = useMemo(
    () =>
      cardData
        ? [
            ['Subscriber', cardData.subscriberName],
            ['Member', cardData.memberName],
            ['Member ID', cardData.memberId],
            ['Policy #', cardData.policyId],
            ['Plan', cardData.planName],
            ['Effective Date', cardData.effectiveDate],
            ['Type / Metal', `${cardData.planType} / ${cardData.metalLevel}`],
            ['Deductible (Ind/Fam)', cardData.medicalDeductible],
            ['OOPM (Ind/Fam)', cardData.annualOopm],
            ['PCP', cardData.pcp],
            ['Specialist', cardData.specialist],
            ['Urgent Care', cardData.urgentCare],
            ['ER', cardData.emergencyRoom],
            ['Rx Generic / Brand', `${cardData.genericRx} / ${cardData.preferredBrandRx}`],
          ].filter(([, value]) => hasDisplayValue(value))
        : [],
    [cardData]
  )

  const backRows = useMemo(() => {
    if (!cardData) return []
    const rows: Array<[string, string]> = [
      ['Carrier', cardData.carrierName],
      ['Member Services', cardData.memberServicesPhone],
      ['Provider', cardData.providerPhone],
      ['Pharmacy', cardData.pharmacyPhone],
      ['Nurse Line', cardData.nurseLine],
      ['Billing', cardData.billingPhone],
      ['Provider Help Desk', cardData.providerHelpDesk],
      ['Prior Auth', cardData.priorAuthPhone],
      ['Care Team', cardData.careTeamPhone],
      ['Mental Health', cardData.mentalHealthPhone],
      ['Medical Claims', cardData.medicalClaimsAddress],
      ['Pharmacy Claims', cardData.pharmacyClaimsAddress],
      ['Claims - Mental Health', cardData.claimsMentalHealth],
      ['Claims - Pharmacy', cardData.claimsPharmacy],
      ['Claims - Pediatric Vision', cardData.claimsPediatricVision],
      ['Claims - Medical', cardData.claimsMedical],
      ['Payer ID', cardData.payerId],
      ['Website', cardData.website],
      ['Provider Portal', cardData.providerPortal],
      ['Issuer TTY', cardData.issuerTty],
      ['Referral', cardData.referralRequired],
      ['Rx BIN', cardData.rxBin],
      ['Rx PCN', cardData.rxPcn],
      ['Rx GRP', cardData.rxGrp],
      ['Benefits URL', cardData.benefitsUrl],
      ['Formulary URL', cardData.formularyUrl],
      ['Network URL', cardData.providerNetworkUrl],
    ]
    cardData.additionalBackLines.forEach((line, index) => rows.push([`Note ${index + 1}`, line]))
    return rows.filter(([, value]) => hasDisplayValue(value))
  }, [cardData])

  const canLookupMarketplace = info ? canLookupMarketplaceCardData(info) : false

  return (
    <Modal show={isOpen} onHide={onClose} backdrop='static' keyboard={false} dialogClassName='modal-wide-sm'>
      <Modal.Header closeButton>
        <Modal.Title>Get member ID card</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{backgroundColor: '#f2f3f8'}} className='modal-body-policy-preview'>
        {loadingPolicy && <div className='text-center'>Loading policy...</div>}
        {errorPolicy && <div className='text-center text-danger'>Error loading policy data.</div>}
        {!loadingPolicy && !errorPolicy && cardData && (
          <Card className='policy-id-card-information'>
            <Card.Body>
              <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2'>
                <div className='small text-muted'>
                  Source:{' '}
                  <strong>
                    {cardData.source === 'LOCAL_AND_MARKETPLACE'
                      ? 'Policy + Marketplace'
                      : 'Policy only'}
                  </strong>
                </div>
                <Button
                  variant='outline-primary'
                  size='sm'
                  onClick={() => copyToClipboard(JSON.stringify(cardData, null, 2), 'ID card data JSON')}
                >
                  Copy card JSON
                </Button>
                <Button
                  variant='primary'
                  size='sm'
                  onClick={() => {
                    const opened = openPrintableMemberIdCard(cardData)
                    if (!opened) toast.error('Please allow pop-ups to print or save the card as PDF.')
                  }}
                >
                  Print / Save PDF
                </Button>
              </div>
              {loadingMarketplace && canLookupMarketplace && (
                <div className='small text-info mb-2'>Loading marketplace plan details...</div>
              )}
              {marketLookupError && (
                <div className='small text-warning mb-2'>
                  Marketplace lookup failed: {marketLookupError}. Showing local fields only.
                </div>
              )}
              {!canLookupMarketplace && info?.CMSPlanId && (
                <div className='small text-warning mb-2'>
                  CMS Plan lookup is only available when state jurisdiction is <strong>FEDERAL</strong>.
                </div>
              )}
              <Row className='g-3'>
                <Col md={6}>
                  <div className='id-card-box'>
                    <div className='id-card-box__title'>Front</div>
                    <div className='id-card-grid'>
                      {frontRows.map(([label, value]) => (
                        <Fragment key={`front-row-${label}`}>
                          <div>{label}</div>
                          <div>{value}</div>
                        </Fragment>
                      ))}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className='id-card-box'>
                    <div className='id-card-box__title'>Back</div>
                    <div className='id-card-grid'>
                      {backRows.map(([label, value]) => (
                        <Fragment key={`back-row-${label}`}>
                          <div>{label}</div>
                          <div>{value}</div>
                        </Fragment>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' size='sm' onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
